import { generalProductCatalog, BATTERY_MODULE_BRUTTO_KWH, systemAdvantages, getDisplayName, GS_NAP_LIMIT_KW } from '../config/general-product-config';
import { privateConsumptionDefaults, injectorYieldDefaults } from '../config/defaults';
import { businessLoadProfiles, businessConsumptionDefaults, calculateDirectConsumptionShareTimeBased, getLoadProfileKey } from '../config/load-profile-config';
import { recommendFactor, classifyFromWizardData } from './sizing-profile-shared';

export function estimateAnnualConsumptionPrivate({ persons, hasEv, hasHeatPump }) {
  const d = privateConsumptionDefaults;
  const p = Math.min(Math.max(persons || 2, 1), 5);
  let base = d.byPersons[p] || d.byPersons[2];
  if (hasEv) base += (d.evKwhByPersons[p] || d.evKwhByPersons[2]);
  if (hasHeatPump) base += (d.heatPumpKwhByPersons[p] || d.heatPumpKwhByPersons[2]);
  return Math.round(base);
}

export function estimateAnnualConsumptionBusiness({ industry, shiftType, weekendOperation, additionalLoadKw = 0 }) {
  const defaults = businessConsumptionDefaults[industry] || businessConsumptionDefaults.other;
  let multiplier = 1.0;
  if (shiftType === 'double') multiplier = 1.5;
  if (shiftType === 'triple') multiplier = 2.0;
  let base = defaults.typicalAnnualKwh * multiplier;
  if (weekendOperation) base *= 1.2;
  base += additionalLoadKw * 2000;
  return Math.round(base);
}

export function deriveLoadProfile({ customerType, industry, shiftType, weekendOperation, hasEv, hasHeatPump }) {
  if (customerType === 'private') {
    let peakFactor = 5.5;
    if (hasHeatPump) peakFactor += 0.8;
    if (hasEv) peakFactor += 0.5;
    return { peakFactor, baseLoadShare: 0.15, activeHoursPerDay: 14, weekendShare: 0.35, type: 'residential' };
  }
  const profile = businessLoadProfiles[industry] || businessLoadProfiles.other;
  let adjusted = { ...profile };
  if (shiftType === 'double') { adjusted.activeHoursPerDay = Math.min(adjusted.activeHoursPerDay * 1.5, 20); adjusted.baseLoadShare = Math.min(adjusted.baseLoadShare * 1.2, 0.6); }
  else if (shiftType === 'triple') { adjusted.activeHoursPerDay = 24; adjusted.baseLoadShare = Math.min(adjusted.baseLoadShare * 1.5, 0.7); }
  if (weekendOperation) adjusted.weekendShare = Math.min(adjusted.weekendShare * 1.5, 0.8);
  return { ...adjusted, type: 'business' };
}

export function estimatePeakLoad({ annualConsumption, loadProfile, hasHeatPump, hasEv, additionalLoadKw = 0 }) {
  const avgPowerKw = annualConsumption / 8760;
  let peakKw;
  if (loadProfile.type === 'residential') {
    peakKw = avgPowerKw * loadProfile.peakFactor;
    if (hasHeatPump) peakKw += 3.0;
    if (hasEv) peakKw += 3.7;
    peakKw += additionalLoadKw * 0.8;
  } else {
    const opHours = loadProfile.activeHoursPerDay * 300;
    peakKw = (annualConsumption / Math.max(opHours, 1)) * loadProfile.peakFactor;
    peakKw += additionalLoadKw;
  }
  return Math.max(Math.round(peakKw * 10) / 10, 1);
}

export function estimateInjectorYield(injector) {
  const d = injectorYieldDefaults;
  if (injector.annualYield && injector.annualYield > 0) return injector.annualYield;
  const power = injector.powerKw || 0;
  switch (injector.type) {
    case 'pv': return power * d.pvYieldPerKwpDefault;
    case 'bhkw': return power * d.bhkwFullLoadHoursDefault;
    case 'wind': return power * d.windYieldFactorDefault;
    case 'fuel_cell': return power * d.fuelCellRunHoursDefault;
    default: return 0;
  }
}

// TIME-BASED direct consumption share using standard profiles
export function estimateDirectConsumptionShare(loadProfile, totalInjectorYield, annualConsumption, customerType, shiftType, uploadedProfile) {
  if (totalInjectorYield === 0 || annualConsumption === 0) return 0;

  // If an uploaded profile is available, use it directly
  if (uploadedProfile && uploadedProfile.hourlyProfile) {
    const pvProfile = [0, 0, 0, 0, 0, 0.005, 0.02, 0.05, 0.08, 0.11, 0.13, 0.14, 0.14, 0.13, 0.11, 0.08, 0.05, 0.02, 0.005, 0, 0, 0, 0, 0];
    const dailyC = annualConsumption / 365;
    const dailyPv = totalInjectorYield / 365;
    let directSum = 0;
    for (let h = 0; h < 24; h++) {
      directSum += Math.min(dailyC * uploadedProfile.hourlyProfile[h], dailyPv * pvProfile[h]);
    }
    return Math.max(0.05, Math.min(0.95, directSum / dailyPv));
  }

  // Use standard profiles with time-based overlap
  const profileKey = getLoadProfileKey(customerType, shiftType);
  return calculateDirectConsumptionShareTimeBased(profileKey, annualConsumption, totalInjectorYield);
}

export function estimateDailyChargeableSurplus(injectors, loadProfile, annualConsumption, customerType, shiftType, uploadedProfile) {
  let totalAnnualYield = 0;
  for (const inj of injectors) totalAnnualYield += estimateInjectorYield(inj);
  if (totalAnnualYield === 0) return 0;
  const directShare = estimateDirectConsumptionShare(loadProfile, totalAnnualYield, annualConsumption, customerType, shiftType, uploadedProfile);
  const dailyYield = totalAnnualYield / 365;
  return Math.round(dailyYield * (1 - directShare) * 100) / 100;
}

export function estimateBackupRequirement({ backupNeedClass, peakLoadKw }) {
  switch (backupNeedClass) {
    case 'none': return 0;
    case 'critical_loads': return peakLoadKw * 0.3 * 4;
    case 'high_backup': return peakLoadKw * 0.6 * 6;
    case 'full_backup': return peakLoadKw * 0.8 * 8;
    default: return 0;
  }
}

export function calculateTargetSystemPower({ scenarioType, backupNeedClass, peakLoadKw, knownPeakLoadKw, injectors }) {
  let targetPower = knownPeakLoadKw || peakLoadKw || 5;
  if (scenarioType === 'backup_only' || (backupNeedClass && backupNeedClass !== 'none')) {
    switch (backupNeedClass) {
      case 'critical_loads': targetPower *= 0.5; break;
      case 'high_backup': targetPower *= 0.75; break;
      case 'full_backup': break;
      default: targetPower *= 0.5;
    }
  }
  const totalInjPow = (injectors || []).reduce((s, i) => s + (i.powerKw || 0), 0);
  targetPower = Math.max(targetPower, totalInjPow * 0.7);
  return Math.round(targetPower * 10) / 10;
}

export function calculateTargetUsableEnergy(data) {
  const { annualConsumption, loadProfile, injectors = [], scenarioType, backupNeedClass, peakLoadKw, customerType, shiftType, uploadedProfile } = data;
  const dailyConsumption = annualConsumption / 365;
  let desiredEnergy = dailyConsumption * 0.4;
  let maxChargeableEnergy = null;
  let warnings = [];

  // ─── PV-kWp-basierte Vor-Auslegung (sizing_profile-Regelwerk) ───────
  // Garantiert identische Ergebnisse zwischen Frag HANS und Konfigurator-
  // Wizard, sofern eine PV-Leistung und eine Profilklasse bekannt sind.
  // §6–§9 aus dem Lastprofil-Regelwerk.
  const pvInjector = injectors.find(i => i?.type === 'pv' && Number(i.powerKw) > 0);
  let profileBaselineKwh = null;
  if (pvInjector && (data.profileClass || customerType)) {
    const cls = data.profileClass
      || classifyFromWizardData(data).profileClass;
    const wt  = data.workTime
      || classifyFromWizardData(data).workTime;
    const flags = data.profileFlags
      || classifyFromWizardData(data).flags;
    const rec = recommendFactor({
      pvKwp: Number(pvInjector.powerKw),
      profileClass: cls,
      workTime: wt,
      flags,
    });
    profileBaselineKwh = rec.usableKwh;
    // Die Profil-basierte Auslegung wird zur HARTEN Untergrenze für das
    // Speicher-Ziel — sonst würden die Konfigurator-Defaults (z. B. 4 Personen
    // → 3.800 kWh/a → 4.86 kWh Speicher) die Hans-Empfehlung unterlaufen.
    desiredEnergy = Math.max(desiredEnergy, profileBaselineKwh);
  }
  // ────────────────────────────────────────────────────────────────────

  if (scenarioType === 'grid_parallel' && injectors.length > 0) {
    const dailySurplus = estimateDailyChargeableSurplus(injectors, loadProfile, annualConsumption, customerType, shiftType, uploadedProfile);
    maxChargeableEnergy = dailySurplus;
    desiredEnergy = Math.min(desiredEnergy, maxChargeableEnergy);
  }

  // Wenn das Lastprofil-Regelwerk eine harte Untergrenze gesetzt hat
  // (profileBaselineKwh, z. B. 9,97 kWh für Villa+Notstrom), darf der oben
  // berechnete Surplus-Cap diese NICHT mehr unterschreiten — sonst weichen
  // Frag HANS und Konfigurator wieder voneinander ab. Wir hängen stattdessen
  // einen Hinweis an, falls die ladbare Tagesmenge unter dem empfohlenen
  // Speicher liegt.
  if (profileBaselineKwh != null) {
    if (maxChargeableEnergy !== null && profileBaselineKwh > maxChargeableEnergy + 0.5) {
      warnings.push(
        `Hinweis: Die rechnerisch tägliche PV-Ladung (≈ ${maxChargeableEnergy.toFixed(1)} kWh) liegt unter dem aus dem Profil-Regelwerk empfohlenen Speicher (${profileBaselineKwh.toFixed(1)} kWh). Bei diesem Lastprofil wird der Speicher in der Praxis selten ganz vollgeladen — die Empfehlung deckt aber den Notstrombedarf und die typischen Verbrauchsspitzen.`
      );
    }
    desiredEnergy = Math.max(desiredEnergy, profileBaselineKwh);
  }

  if (scenarioType === 'backup_only') {
    const backupReq = estimateBackupRequirement({ backupNeedClass: backupNeedClass || 'critical_loads', peakLoadKw });
    desiredEnergy = Math.max(desiredEnergy, backupReq);
  }

  if (scenarioType === 'grid_parallel' && backupNeedClass && backupNeedClass !== 'none') {
    const backupReq = estimateBackupRequirement({ backupNeedClass, peakLoadKw });
    if (maxChargeableEnergy !== null && backupReq > maxChargeableEnergy) {
      warnings.push("Der gewünschte Backup-/Netzersatzbedarf liegt über der mit den angegebenen Einspeisern realistisch ladbaren Speichermenge. Zusätzliche Erzeugerleistung oder eine Projektprüfung kann sinnvoll sein.");
    }
    desiredEnergy = Math.max(desiredEnergy, Math.min(backupReq * 0.5, maxChargeableEnergy || backupReq));
  }

  desiredEnergy = Math.max(desiredEnergy, 4.864);
  return {
    targetUsableEnergyKwh: Math.round(desiredEnergy * 100) / 100,
    maxChargeableEnergyKwh: maxChargeableEnergy !== null ? Math.round(maxChargeableEnergy * 100) / 100 : null,
    isCapped: maxChargeableEnergy !== null && desiredEnergy >= maxChargeableEnergy - 0.1,
    profileBaselineKwh,
    warnings
  };
}

export function getGeneralSystemCandidates(data) {
  const { customerType, scenarioType, backupNeedClass, injectors = [], pvComplexityClass, targetPowerKw } = data;
  const hasPv = injectors.some(i => i.type === 'pv');
  const pvPlanned = injectors.some(i => i.type === 'pv' && i.status === 'planned');
  const pvExisting = injectors.some(i => i.type === 'pv' && i.status === 'existing');
  const hasBhkw = injectors.some(i => i.type === 'bhkw');
  const hasGenerator = injectors.some(i => i.type === 'generator');
  const genInjector = injectors.find(i => i.type === 'generator');
  const genPhase = genInjector?.generatorPhase || null;
  const isSinglePhaseGen = genPhase === 'single_phase';
  const isThreePhaseGen = genPhase === 'three_phase';

  // RULE: GS only for Gewerbe
  if (customerType === 'business') {
    const totalInjectorKw = injectors.reduce((s, i) => s + (i.powerKw || 0), 0);
    const maxSystemKw = GS_NAP_LIMIT_KW - totalInjectorKw;

    if (targetPowerKw > 90 || maxSystemKw < 12) {
      return { candidates: [], isProjectPath: true, projectNote: `Für Projekte dieser Größenordnung empfehlen wir die Hans GS-C HVP Containerlösung. Bitte kontaktieren Sie uns unter order@ees-energiespeicher.de.`, napLimit: maxSystemKw };
    }

    let gsCandidates = generalProductCatalog.filter(p => p.customerSegment === 'business' && p.maxDischargeKw <= maxSystemKw);
    let napWarning = null;
    if (gsCandidates.length === 0 || (targetPowerKw > Math.max(...gsCandidates.map(c => c.maxDischargeKw)))) {
      napWarning = `Die 135-kW-Grenze am Netzanschlusspunkt (VDE 4105) begrenzt die maximale Systemleistung auf ${Math.round(maxSystemKw)} kW (135 kW abzüglich ${Math.round(totalInjectorKw)} kW Einspeiserleistung). Für größere Systeme empfehlen wir die Hans GS-C HVP Reihe. Anfrage unter order@ees-energiespeicher.de.`;
    }
    let gsGeneratorWarning = null;
    if (hasGenerator) {
      gsGeneratorWarning = "Für den Generatorbetrieb am GS ist eine zusätzliche, externe Netzumschaltung notwendig.";
    }
    return { candidates: gsCandidates.length > 0 ? gsCandidates : generalProductCatalog.filter(p => p.customerSegment === 'business'), isProjectPath: false, napWarning, maxSystemKw, gsGeneratorWarning };
  }

  // PRIVATE
  let privateSystems = generalProductCatalog.filter(p => p.customerSegment === 'private');

  // RULE: "Vollwertiger Netzersatz 400V" = only 3-phase
  if (backupNeedClass === 'full_backup') {
    privateSystems = privateSystems.filter(p => p.phases >= 3);
  }
  // RULE: Three-phase generator = only 3-phase systems
  if (isThreePhaseGen) {
    privateSystems = privateSystems.filter(p => p.phases >= 3);
  }
  // RULE (User 03.08.26): BHKW + Vollnetzersatz → mindestens HS 15
  // Begründung: BHKW muss zuverlässig eingespeist werden können; HS-3/5/6/9
  // haben zu geringe Ladeleistung. Wir filtern auf die HS-15-Klasse (alle
  // Familien: HS, HS PV, HS PV duo — je nachdem was sonst passt).
  if (hasBhkw && backupNeedClass === 'full_backup') {
    const HS15_MIN_DISCHARGE_KW = 12.6;
    privateSystems = privateSystems.filter(p => (p.maxDischargeKw || 0) >= HS15_MIN_DISCHARGE_KW);
  }

  // Multi-Injector-Overload-Check (User 03.08.26): wenn die kombinierte
  // Ladeleistung aller Einspeiser die max. Ladeleistung des größten HS-
  // Systems um > 20 % überschreitet, ist die HS-Familie schlicht zu klein.
  // Wir schlagen dem Kunden — auch bei „Privat" — eine passende GS-Variante
  // vor. Dieser Hinweis wird durchgereicht in `gsRecommendationForPrivate`.
  const HS_MAX_DISCHARGE_KW = 12.6;   // HS 15 = Top-Modell der Privat-Reihe
  const totalInjectorKw = (injectors || []).reduce((s, i) => s + (Number(i.powerKw) || 0), 0);
  let gsRecommendationForPrivate = null;
  if (totalInjectorKw > HS_MAX_DISCHARGE_KW * 1.20) {
    const gsCandidates = generalProductCatalog
      .filter(p => p.family === 'GS' && (p.maxDischargeKw || 0) >= totalInjectorKw / 1.20)
      .sort((a, b) => a.maxDischargeKw - b.maxDischargeKw);
    const suggested = gsCandidates[0] || null;
    gsRecommendationForPrivate = {
      totalInjectorKw: Math.round(totalInjectorKw * 10) / 10,
      hsMaxKw: HS_MAX_DISCHARGE_KW,
      suggestedGsModel: suggested?.model || null,
      suggestedGsKw: suggested?.maxDischargeKw || null,
      message: suggested
        ? `Ihre Einspeiser summieren sich auf ${Math.round(totalInjectorKw * 10) / 10} kW — das überschreitet die maximale Ladeleistung der HS-Reihe (${HS_MAX_DISCHARGE_KW} kW) um mehr als 20 %. Wir empfehlen einen Wechsel zur Gewerbespeicher-Reihe: ${suggested.model} (${suggested.maxDischargeKw} kW). Kontakt: order@ees-energiespeicher.de.`
        : `Ihre Einspeiser summieren sich auf ${Math.round(totalInjectorKw * 10) / 10} kW und überschreiten die HS-Reihe (${HS_MAX_DISCHARGE_KW} kW). Für diese Größenordnung empfehlen wir die Hans GS-C HVP Container-Lösung. Anfrage: order@ees-energiespeicher.de.`,
    };
  }

  const _wrap = (obj) => ({ ...obj, gsRecommendationForPrivate });

  if (pvPlanned && pvComplexityClass === 'complex') {
    return _wrap({ candidates: privateSystems.filter(p => p.family === 'HS PV duo'), isProjectPath: false, isSinglePhaseGen });
  }
  if (pvPlanned && pvComplexityClass !== 'complex') {
    return _wrap({ candidates: privateSystems.filter(p => p.family === 'HS PV'), isProjectPath: false, isSinglePhaseGen });
  }
  if (pvExisting || hasBhkw || hasGenerator) {
    return _wrap({ candidates: privateSystems.filter(p => p.family === 'HS'), isProjectPath: false, isSinglePhaseGen });
  }
  if (scenarioType === 'backup_only' && !hasPv && targetPowerKw < 5) {
    return _wrap({ candidates: privateSystems.filter(p => p.family === 'LS' || p.family === 'HS'), isProjectPath: false, isSinglePhaseGen });
  }
  return _wrap({ candidates: privateSystems.filter(p => p.family === 'HS'), isProjectPath: false, isSinglePhaseGen });
}

// ─────────────────────────────────────────────────────────────
// Ladeleistungs-Prüfung („Ladeleistung AC max. kW 1)" + „Ladeleistung PV-DC max. kW 1)"
// bzw. „Ladeleistung PV kombiniert max. kW 1)")
// ─────────────────────────────────────────────────────────────
// Regel (User 27.02.2026):
//   Wenn ein BHKW ODER Generator eingegeben wird, muss die AC-Ladeleistung
//   des Systems mindestens 90 % der eingegebenen BHKW-/Generator-Leistung
//   entsprechen. Bei einer Bestands-PV (AC-gekoppelt via externem WR) wird
//   deren Leistung zur BHKW-/Generator-Summe addiert.
//   Bei einer neu geplanten PV, die in ein HS PV / HS PV duo integriert wird
//   (DC-Kopplung), zählt für AC nur BHKW + Bestands-PV; zusätzlich muss die
//   PV-DC-Kapazität (HS PV) bzw. PV-kombiniert-Kapazität (HS PV duo) die
//   PV-Leistung tragen können.
export function checkChargePowerConstraint(system, injectors = []) {
  if (!system) return { applies: false, passes: true };
  const bhkwKw = (injectors || [])
    .filter(i => i?.type === 'bhkw')
    .reduce((s, i) => s + (Number(i.powerKw) || 0), 0);
  const genKw = (injectors || [])
    .filter(i => i?.type === 'generator')
    .reduce((s, i) => s + (Number(i.powerKw) || 0), 0);
  const existingPvKw = (injectors || [])
    .filter(i => i?.type === 'pv' && i?.status === 'existing')
    .reduce((s, i) => s + (Number(i.powerKw) || 0), 0);
  const plannedPvKw = (injectors || [])
    .filter(i => i?.type === 'pv' && i?.status === 'planned')
    .reduce((s, i) => s + (Number(i.powerKw) || 0), 0);

  const fam = (system.family || '').toString();
  const isHsPvHybrid = fam === 'HS PV' || fam === 'HS PV duo';

  // Regel greift NUR wenn ein BHKW ODER Generator angegeben wurde.
  // (User 27.02.2026: "wenn ein BHKW gewählt wird oder Generator …
  //  90 % der eingegebenen BHKW/Generator Leistung"). Reine PV-Szenarien
  // ohne BHKW/Generator werden davon nicht berührt.
  const hasBhkwOrGen = bhkwKw > 0 || genKw > 0;
  if (!hasBhkwOrGen) return { applies: false, passes: true };

  // ─── AC-Ladung ──────────────────────────────────────────────
  // HS PV / duo: neu geplante PV geht via DC-MPPT → nicht in AC einrechnen
  // HS / GS: alles läuft AC-seitig → geplante PV wird mit einbezogen
  const acLoadKw = isHsPvHybrid
    ? (bhkwKw + genKw + existingPvKw)
    : (bhkwKw + genKw + existingPvKw + plannedPvKw);
  const acRequiredKw = acLoadKw * 0.9;
  const acAvailableKw = Number(system.maxChargeAcKw ?? system.maxDischargeKw ?? 0);
  const acPasses = acRequiredKw <= 0 || acAvailableKw + 1e-3 >= acRequiredKw;

  // ─── PV-DC-Ladung (nur HS PV / HS PV duo bei neu geplanter PV) ─
  let dcRequiredKw = 0;
  let dcAvailableKw = 0;
  let dcLabel = null;
  let dcPasses = true;
  if (isHsPvHybrid && plannedPvKw > 0) {
    dcRequiredKw = plannedPvKw;
    if (fam === 'HS PV duo') {
      dcAvailableKw = Number(system.maxChargePvCombinedKw || 0);
      dcLabel = 'PV kombiniert';
    } else {
      dcAvailableKw = Number(system.maxChargePvDcKw || 0);
      dcLabel = 'PV-DC';
    }
    dcPasses = dcAvailableKw + 1e-3 >= dcRequiredKw;
  }

  const r1 = (v) => Math.round(v * 10) / 10;
  return {
    applies: true,
    passes: acPasses && dcPasses,
    acPasses,
    dcPasses,
    acRequiredKw: r1(acRequiredKw),
    acAvailableKw: r1(acAvailableKw),
    acLoadKw: r1(acLoadKw),
    dcRequiredKw: r1(dcRequiredKw),
    dcAvailableKw: r1(dcAvailableKw),
    dcLabel,
    bhkwKw: r1(bhkwKw),
    genKw: r1(genKw),
    existingPvKw: r1(existingPvKw),
    plannedPvKw: r1(plannedPvKw),
  };
}

export function scoreGeneralSystemCandidates(candidates, targetPowerKw, targetEnergyKwh) {
  return candidates.map(c => {
    let score = 100;
    const powerRatio = c.maxDischargeKw / Math.max(targetPowerKw, 0.1);
    // Power scoring — continuous, larger systems preferred when all are too small
    if (powerRatio >= 0.9 && powerRatio <= 1.3) score += 25;
    else if (powerRatio >= 0.7 && powerRatio < 0.9) score += 10;
    else if (powerRatio > 1.3 && powerRatio <= 2.0) score += 5;
    else if (powerRatio < 0.7) score -= 30 * (1 - powerRatio);
    else score -= 10;

    const energyRatio = c.standardUsableKwh / Math.max(targetEnergyKwh, 0.1);
    if (energyRatio >= 0.7 && energyRatio <= 2.0) score += 10;
    else if (energyRatio < 0.7) score -= 15 * (1 - energyRatio);
    else score -= 10;

    if (powerRatio >= 0.9 && powerRatio <= 1.3) score += 10;
    return { ...c, score, powerRatio, energyRatio };
  }).sort((a, b) => b.score - a.score);
}

export function buildGeneralRecommendation(data) {
  const { annualConsumption, customerType, scenarioType, backupNeedClass, injectors = [],
    generatorType, pvComplexityClass, hasEv, hasHeatPump, loadProfile, shiftType, uploadedProfile } = data;

  const peakLoadKw = data.peakLoadKw || estimatePeakLoad({ annualConsumption, loadProfile, hasHeatPump: hasHeatPump || false, hasEv: hasEv || false });
  let targetPowerKw = calculateTargetSystemPower({ scenarioType, backupNeedClass, peakLoadKw, knownPeakLoadKw: data.knownPeakLoadKw, injectors });
  const energyResult = calculateTargetUsableEnergy({ annualConsumption, loadProfile, injectors, scenarioType, backupNeedClass, peakLoadKw, customerType, shiftType, uploadedProfile });
  const { candidates, isProjectPath, projectNote, napWarning, maxSystemKw, gsGeneratorWarning, isSinglePhaseGen, gsRecommendationForPrivate } = getGeneralSystemCandidates({ customerType, scenarioType, backupNeedClass, injectors, pvComplexityClass, targetPowerKw });

  // Cap targetPowerKw at NAP limit for Gewerbe
  if (customerType === 'business' && maxSystemKw && targetPowerKw > maxSystemKw) {
    targetPowerKw = maxSystemKw;
  }

  if (isProjectPath) {
    return { isProjectPath: true, projectNote, targetPowerKw, targetEnergyKwh: energyResult.targetUsableEnergyKwh, warnings: energyResult.warnings };
  }

  const scored = scoreGeneralSystemCandidates(candidates, targetPowerKw, energyResult.targetUsableEnergyKwh);
  if (scored.length === 0) {
    return { isProjectPath: true, projectNote: "Für Ihre Anforderungen konnte kein passendes Standardsystem ermittelt werden. Bitte kontaktieren Sie uns für eine individuelle Projektberatung.", targetPowerKw, targetEnergyKwh: energyResult.targetUsableEnergyKwh, warnings: energyResult.warnings };
  }

  // ─── Ladeleistungs-Constraint (BHKW/Generator + PV) ───────────────
  // Nur relevant wenn ein BHKW oder Generator eingespeist wurde. Bei reinen
  // PV-Szenarien greift die Regel NICHT (siehe checkChargePowerConstraint).
  // Passing-Systeme werden nach oben sortiert. Wenn KEIN Kandidat in der
  // aktuell gefilterten Familie passt, erweitern wir gezielt:
  //   • HS PV → HS PV duo (Dual-MPPT trägt größere DC-Kapazität)
  //   • HS PV duo mit voller PV → GS-Familie (AC-Ladeleistung > 12,3 kW)
  //   • HS  → HS PV duo (falls Bestands-WR + neue DC-PV kombiniert wird)
  let chargeConstraintUpgradeMsg = null;
  const _hasBhkwOrGen = (injectors || []).some(i =>
    (i?.type === 'bhkw' || i?.type === 'generator') && (Number(i.powerKw) || 0) > 0);
  if (_hasBhkwOrGen) {
    const _hasPassing = scored.some(s => checkChargePowerConstraint(s, injectors).passes);
    if (!_hasPassing) {
      // Kandidatenpool erweitern (HS PV duo + größere HS + ggf. GS-Serie).
      const extraFamilies = new Set();
      const scoredFamilies = new Set(scored.map(s => (s.family || '').toString()));
      if (scoredFamilies.has('HS PV')) extraFamilies.add('HS PV duo');
      if (scoredFamilies.has('HS PV duo')) extraFamilies.add('GS');
      if (scoredFamilies.has('HS')) { extraFamilies.add('HS PV duo'); extraFamilies.add('GS'); }
      const extraCandidates = generalProductCatalog.filter(p => extraFamilies.has(p.family)
        // Für Privat keine GS-Modelle aufnehmen, außer wenn kein anderes System passt.
        && (customerType === 'business' || !p.family.startsWith('GS')
            || checkChargePowerConstraint(p, injectors).passes)
      );
      if (extraCandidates.length > 0) {
        const enriched = scoreGeneralSystemCandidates(extraCandidates, targetPowerKw, energyResult.targetUsableEnergyKwh);
        const nowPassing = enriched.filter(s => checkChargePowerConstraint(s, injectors).passes);
        if (nowPassing.length > 0) {
          // Modelle deduplizieren (Score behalten) und an den Anfang setzen
          const seen = new Set(scored.map(s => s.model));
          nowPassing.forEach(s => { if (!seen.has(s.model)) { scored.unshift(s); seen.add(s.model); } });
          const upgradeTarget = nowPassing[0];
          chargeConstraintUpgradeMsg = `Auf Basis Ihrer Einspeiser (BHKW/Generator/PV) wurde die Empfehlung auf „${getDisplayName(upgradeTarget.model)}" hochgestuft, damit die AC-Ladeleistung (≥ 90 % der Einspeiser) und die PV-DC-Kapazität sicher tragen.`;
        }
      }
    }
    // Passing-Systeme grundsätzlich nach vorne sortieren (stabile Sortierung).
    scored.sort((a, b) => {
      const pa = checkChargePowerConstraint(a, injectors).passes ? 1 : 0;
      const pb = checkChargePowerConstraint(b, injectors).passes ? 1 : 0;
      return pb - pa;   // passing zuerst
    });
  }

  // Nach-Ranking für Wohngebäude mit höherer Last und Notstromwunsch:
  //   • Villa/Altbau   → mind. dreiphasiges HS 9 PV (statt HS 6 PV + Modul)
  //   • Mehrfamilienhaus → mind. dreiphasiges HS 15 PV (mehrere Parteien,
  //     höhere Gleichzeitigkeitslast, Allgemeinstrom + Wohneinheiten)
  // Für ein normales EFH (auch mit Notstrom) ist HS 6 PV + Modul ideal.
  const isVilla = data.profileClass === 'private_villa'
    || (customerType === 'private' && (data.persons || 0) >= 5);
  const isMFH = data.profileClass === 'private_mfh';
  const needsBackup = backupNeedClass && backupNeedClass !== 'none';

  // Hilfsfunktion: kleinstes "geeignetes" Modell-Floor je Profilklasse
  // (entspricht §20 Mehrfamilienhaus-Logik aus dem Regelwerk)
  const minModelFloor = isMFH && needsBackup ? ['HS 15 PV', 'HS 15 PV duo', 'HS 15']
    : isVilla && needsBackup ? ['HS 9 PV', 'HS 9 PV duo', 'HS 15 PV', 'HS 15 PV duo', 'HS 15', 'HS 9']
    : null;

  if (minModelFloor && scored.length > 1) {
    const topNeedsModules = scored[0].standardUsableKwh < energyResult.targetUsableEnergyKwh;
    const topBelowFloor = !minModelFloor.includes(scored[0].model);
    if (topNeedsModules || topBelowFloor) {
      // Direct-Fit innerhalb der "Floor"-Liste, mit ausreichender Leistung.
      const directFit = scored.find((s) =>
        minModelFloor.includes(s.model)
        && s.standardUsableKwh >= energyResult.targetUsableEnergyKwh * 0.7
        && s.powerRatio >= 0.5
        && s.standardUsableKwh <= Math.max(energyResult.targetUsableEnergyKwh, 8) * 3.0
      );
      if (directFit) {
        const idx = scored.indexOf(directFit);
        scored.splice(idx, 1);
        scored.unshift(directFit);
      }
    }
  }

  let variantA = scored[0];
  let variantB = scored.length > 1 ? scored[1] : null;

  // Frag-HANS-Override: wenn Chat-Karte ein konkretes Modell empfohlen hat
  // (z. B. „HS 6 PV" aufgrund 10 PV-Modulen), MUSS die Slider-Modus-
  // Ergebnisseite dasselbe Modell als Primärempfehlung zeigen — sonst
  // entsteht Desync zwischen Chat-Empfehlung und Result-Header
  // (User-Bug-Report 2026-06-22). Override greift nur, wenn das Modell
  // im Catalog existiert; bevorzugt aus der gescorten Liste (= passt zum
  // Filter-Kontext: customerType, scenarioType, NAP-Limits).
  const overrideModel = (data.recommendedModelOverride || '').trim();
  if (overrideModel) {
    const matchInScored = scored.find(
      (s) => (s.model || '').toLowerCase() === overrideModel.toLowerCase()
    );
    if (matchInScored) {
      const previousA = variantA;
      variantA = matchInScored;
      // Zweitbeste Wahl als Alternative behalten, aber nicht das override-Modell selbst
      variantB = previousA && previousA.model !== matchInScored.model
        ? previousA : (variantB && variantB.model !== matchInScored.model ? variantB : null);
    } else {
      // Fallback: direkt aus Catalog mit Score-Feldern anreichern.
      const catalogHit = generalProductCatalog.find(
        (p) => (p.model || '').toLowerCase() === overrideModel.toLowerCase()
      );
      if (catalogHit) {
        const enriched = scoreGeneralSystemCandidates(
          [catalogHit],
          targetPowerKw,
          energyResult.targetUsableEnergyKwh,
        )[0];
        if (enriched) {
          const previousA = variantA;
          variantA = enriched;
          variantB = previousA;
        }
      }
    }
  }

  // For Privat: if the best alternative is very underpowered, check if a GS system would be a better alternative
  let gsAlternativeNote = null;
  if (customerType === 'private' && variantA.powerRatio < 1.5) {
    const gsSystems = generalProductCatalog.filter(p => p.customerSegment === 'business');
    const gsScored = scoreGeneralSystemCandidates(gsSystems, targetPowerKw, energyResult.targetUsableEnergyKwh);
    if (gsScored.length > 0 && (!variantB || gsScored[0].score > variantB.score)) {
      variantB = gsScored[0];
      gsAlternativeNote = "Für diesen Leistungsbedarf könnte auch ein Gewerbesystem sinnvoll sein.";
    }
  }

  const calcAdditional = (system) => {
    if (energyResult.targetUsableEnergyKwh > system.standardUsableKwh) {
      const additionalModules = Math.ceil((energyResult.targetUsableEnergyKwh - system.standardUsableKwh) / BATTERY_MODULE_BRUTTO_KWH);
      return { modules: additionalModules, bruttoKwh: additionalModules * BATTERY_MODULE_BRUTTO_KWH };
    }
    return null;
  };

  const advantages = systemAdvantages[variantA.family] || systemAdvantages.HS;
  let warnings = [...energyResult.warnings];
  if (napWarning) warnings.push(napWarning);
  if (gsGeneratorWarning) warnings.push(gsGeneratorWarning);
  if (gsAlternativeNote) warnings.push(gsAlternativeNote);
  if (chargeConstraintUpgradeMsg) warnings.push(chargeConstraintUpgradeMsg);

  // Ladeleistungs-Constraint für variantA prüfen — Warnung, wenn selbst das
  // hochskalierte System die 90 %-Regel oder die PV-DC-Kapazität nicht trägt.
  const chargeCheck = checkChargePowerConstraint(variantA, injectors);
  if (chargeCheck.applies && !chargeCheck.passes) {
    const parts = [];
    if (!chargeCheck.acPasses) {
      parts.push(
        `AC-Ladeleistung: benötigt ≥ ${chargeCheck.acRequiredKw} kW (90 % der Einspeiser: `
        + `${chargeCheck.bhkwKw > 0 ? `BHKW ${chargeCheck.bhkwKw} kW` : ''}`
        + `${chargeCheck.genKw > 0 ? `${chargeCheck.bhkwKw > 0 ? ' + ' : ''}Generator ${chargeCheck.genKw} kW` : ''}`
        + `${chargeCheck.existingPvKw > 0 ? ` + Bestands-PV ${chargeCheck.existingPvKw} kWp` : ''}`
        + `${chargeCheck.plannedPvKw > 0 && (variantA.family !== 'HS PV' && variantA.family !== 'HS PV duo') ? ` + Neu-PV ${chargeCheck.plannedPvKw} kWp` : ''}`
        + `), System liefert ${chargeCheck.acAvailableKw} kW.`
      );
    }
    if (!chargeCheck.dcPasses) {
      parts.push(
        `${chargeCheck.dcLabel}-Kapazität: benötigt ${chargeCheck.dcRequiredKw} kW (Neu-PV), `
        + `System liefert ${chargeCheck.dcAvailableKw} kW.`
      );
    }
    warnings.push(
      `Achtung Ladeleistung: Das gewählte System trägt Ihre Einspeiser nicht vollständig — `
      + parts.join(' ')
      + ' Für dieses Projekt empfehlen wir eine individuelle Auslegung (order@ees-energiespeicher.de).'
    );
  }

  // GS + NEUE PV → Hinweis auf PV-Einbindungs-Optionen.
  // Greift bei jeder GS-Familienempfehlung (GS / GS HV / GS-C HVP), sobald
  // mindestens ein PV-Einspeiser als "planned" gemeldet wurde. Bei einer
  // bestehenden PV-Anlage (status='existing') ist der externe Wechselrichter
  // bereits vorhanden — dann KEIN Hinweis.
  const variantAFamily = (variantA.family || '').toString().toUpperCase();
  const hasPlannedPv = (injectors || []).some(i => i?.type === 'pv' && i?.status === 'planned');
  const hasExistingPv = (injectors || []).some(i => i?.type === 'pv' && i?.status === 'existing');
  if (variantAFamily.startsWith('GS') && hasPlannedPv && !hasExistingPv) {
    warnings.push(
      'PV-Einbindung am GS (Neuanlage): Da die PV-Anlage neu errichtet wird, ist entweder ein PV-Direkteinbindungsmodul für den Hans GS notwendig oder ein externer PV-AC-Wechselrichter. Das PV-Direkteinbindungsmodul ist projektspezifisch — bitte über EES (order@ees-energiespeicher.de) anfragen.'
    );
  }

  // GenLink note: single-phase generator + three-phase system recommended
  const genLinkNeeded = isSinglePhaseGen && variantA.phases >= 3;
  if (genLinkNeeded) warnings.push("Bei Einsatz eines einphasigen Generators wird die GenLink-Funktion für die dreiphasige Netzersatzumschaltung benötigt.");

  // RULE: If recommendation is the largest in its family, no alternative
  const isLargestPrivate = customerType === 'private' && (variantA.model === 'HS 15' || variantA.model === 'HS 15 PV' || variantA.model === 'HS 15 PV duo');
  const isLargestBusiness = customerType === 'business' && variantA.model === 'GS 90';
  if (isLargestPrivate || isLargestBusiness) {
    variantB = null;
    warnings.push('Die Systemempfehlung ist bereits das größtmögliche System der Produktfamilie. Eine sinnvolle Alternative kann daher nicht aufgezeigt werden.');
  }

  const reasoning = buildReasoning(data, variantA, targetPowerKw, energyResult);

  return {
    isProjectPath: false,
    variantA: { ...variantA, additionalBattery: calcAdditional(variantA), displayName: getDisplayName(variantA.model) },
    variantB: variantB ? { ...variantB, additionalBattery: calcAdditional(variantB), displayName: getDisplayName(variantB.model) } : null,
    targetPowerKw, targetEnergyKwh: energyResult.targetUsableEnergyKwh,
    maxChargeableEnergyKwh: energyResult.maxChargeableEnergyKwh,
    isCapped: energyResult.isCapped, advantages, warnings, reasoning,
    genLink: genLinkNeeded || false,
    gsRecommendationForPrivate: gsRecommendationForPrivate || null,
    chargePowerCheck: chargeCheck,
  };
}

function buildReasoning(data, system, targetPowerKw, energyResult) {
  const parts = [];
  if (data.customerType === 'private') {
    parts.push(`Basierend auf Ihrem ${data.consumptionEstimated ? 'geschätzten ' : ''}Jahresverbrauch von ca. ${Math.round(data.annualConsumption).toLocaleString('de-DE')} kWh`);
  } else {
    parts.push(`Basierend auf dem angegebenen Jahresverbrauch und Ihrem Branchenprofil`);
  }
  if (data.scenarioType === 'grid_parallel') {
    parts.push("im Netzparallelbetrieb");
    if (data.backupNeedClass && data.backupNeedClass !== 'none') parts.push("mit Netzersatzfunktion");
  } else parts.push("als Notstrom-/Backupsystem");
  if (data.injectors?.length > 0) {
    const pvInj = data.injectors.filter(i => i.type === 'pv');
    if (pvInj.length > 0) {
      const totalPvKw = pvInj.reduce((s, i) => s + (i.powerKw || 0), 0);
      parts.push(`mit ${totalPvKw} kWp PV-Leistung`);
    }
  }
  parts.push(`empfehlen wir das ${getDisplayName(system.model)} (Systemleistung ${system.maxDischargeKw} kW) mit einem empfohlenen Energieinhalt von ca. ${energyResult.targetUsableEnergyKwh} kWh.`);
  if (energyResult.isCapped) parts.push("Die empfohlene Speicherkapazität wurde an der realistisch ladbaren Einspeisemenge ausgerichtet.");
  return parts.join(" ");
}
